// Dad Profile Modal Component — Allows viewing any Dad and friending them into your Crew
import type { DadProfile, BadgeDefinition } from '../types';
import { getDadDisplayName } from '../types';
import { getCrewRelation, sendCrewRequest, acceptCrewRequest, declineCrewRequest, cancelCrewRequest, removeFromCrew } from '../services/crewService';
import { getCurrentProfile } from '../services/authService';
import { isAdmin } from '../services/adminService';
import { showToast } from '../components/Toast';

export function renderDadProfileModal(targetUser: DadProfile, badgeMasterList: BadgeDefinition[] = []): string {
  const relation = getCrewRelation(targetUser.uid);
  const earnedBadges = badgeMasterList.filter(b => targetUser.badgesEarned?.includes(b.id));

  const current = getCurrentProfile();
  const isSelf = current?.uid === targetUser.uid;
  const isAdministrator = isAdmin();
  const canViewFullDetails = isSelf || isAdministrator;

  const displayName = getDadDisplayName(targetUser);

  return `
    <div class="modal-backdrop fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in font-sans">
      <div class="card-workshop max-w-xl w-full max-h-[90vh] overflow-y-auto p-6 sm:p-7 relative border-white/[0.12] bg-[#090b12] space-y-5">
        <div class="laser-line-cyan absolute top-0 left-0 right-0"></div>

        <!-- Top Bar -->
        <div class="flex items-center justify-between pb-3 border-b border-white/[0.08]">
          <div class="flex items-center gap-2">
            <span class="text-xs uppercase tracking-wider font-bold text-cyan-400 flex items-center gap-1.5">
              <i class="fa-solid fa-id-card"></i> ${canViewFullDetails ? 'Dad Resume' : 'Member Profile'}
            </span>
            ${isAdministrator ? `
              <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-400/40">
                <i class="fa-solid fa-shield-halved mr-1"></i> Admin View
              </span>
            ` : ''}
          </div>
          <button id="close-dad-profile-modal-btn" class="text-slate-400 hover:text-white p-1 text-lg transition-colors cursor-pointer">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <!-- Hero Header -->
        <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div class="flex items-center gap-4">
            <div class="w-16 h-16 sm:w-18 sm:h-18 rounded-2xl bg-transparent text-cyan-400 font-black text-2xl flex items-center justify-center border-2 border-cyan-400 shadow-[0_0_15px_rgba(0,240,255,0.35)] flex-shrink-0">
              ${targetUser.avatarInitials}
            </div>
            <div>
              <div class="flex items-center gap-2 flex-wrap">
                <h2 class="text-xl sm:text-2xl font-black text-slate-100">${displayName}</h2>
                <span class="w-2.5 h-2.5 rounded-full ${targetUser.status === 'active' ? 'bg-emerald-400 shadow-[0_0_8px_rgba(0,255,136,0.6)]' : 'bg-slate-600'}" title="${targetUser.status === 'active' ? 'Active' : 'Offline'}"></span>
              </div>
              ${canViewFullDetails && targetUser.displayName ? `
                <p class="text-[11px] text-slate-400">Google Name: <span class="text-slate-300">${targetUser.name}</span></p>
              ` : ''}
              ${canViewFullDetails ? `
                <p class="text-xs text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
                  <span><i class="fa-solid fa-location-dot text-cyan-400 mr-1"></i> ${targetUser.neighborhood || 'Local Area'}</span>
                  <span>&bull;</span>
                  <span class="text-slate-300 font-semibold">${targetUser.yearsAsDad} Years as a Dad</span>
                </p>
                ${targetUser.beverage ? `
                  <p class="text-xs text-pink-400 mt-1 flex items-center gap-1.5 font-medium">
                    <i class="fa-solid fa-mug-hot text-pink-400"></i>
                    <span>Beverage: ${targetUser.beverage}</span>
                  </p>
                ` : ''}
                ${isAdministrator ? `
                  <p class="text-[11px] text-amber-300/90 mt-1 flex items-center gap-1 font-mono">
                    <i class="fa-solid fa-envelope text-[10px]"></i> ${targetUser.email}
                  </p>
                ` : ''}
              ` : `
                <p class="text-xs text-slate-400 mt-1 flex items-center gap-1.5">
                  <i class="fa-solid fa-circle-check text-cyan-400 text-[11px]"></i>
                  <span>Pops Culture Community Member</span>
                </p>
              `}
            </div>
          </div>

          <!-- Mini Badges & Friends Counter -->
          <div class="flex items-center gap-2 self-start sm:self-center">
            <div class="text-center px-3 py-1.5 rounded-xl bg-transparent border border-white/[0.08] min-w-[60px]">
              <div class="text-base font-black text-cyan-400">${targetUser.badgesEarned?.length || 0}</div>
              <div class="text-[9px] text-slate-400 uppercase font-bold tracking-wider">Badges</div>
            </div>
            <div class="text-center px-3 py-1.5 rounded-xl bg-transparent border border-white/[0.08] min-w-[60px]">
              <div class="text-base font-black text-cyan-400">${targetUser.crew?.length || 0}</div>
              <div class="text-[9px] text-slate-400 uppercase font-bold tracking-wider">Crew</div>
            </div>
          </div>
        </div>

        <!-- Crew (Friendship) Action Bar -->
        <div class="pt-2">
          ${relation === 'self' ? `
            <div class="p-3 bg-cyan-950/30 border border-cyan-400/30 rounded-xl flex items-center gap-2 text-xs text-cyan-300">
              <i class="fa-solid fa-user-check text-cyan-400 text-sm"></i>
              <span>This is your own Dad Resume.</span>
            </div>
          ` : relation === 'in_crew' ? `
            <div class="p-3.5 bg-[#09101d] border border-cyan-400/50 rounded-xl flex items-center justify-between gap-3 shadow-[0_0_12px_rgba(0,240,255,0.15)]">
              <div class="flex items-center gap-2">
                <span class="px-2.5 py-1 rounded-md text-xs font-bold bg-cyan-950/80 border border-cyan-400 text-cyan-300 shadow-[0_0_8px_rgba(0,240,255,0.3)] flex items-center gap-1.5">
                  <i class="fa-solid fa-users text-cyan-400"></i> In Your Crew
                </span>
                <span class="text-xs text-slate-300 hidden sm:inline">You are mutual friends!</span>
              </div>
              <button id="profile-remove-crew-btn" class="px-3 py-1.5 rounded-lg text-xs font-bold text-pink-400 border border-pink-500/40 hover:border-pink-500 hover:bg-pink-950/30 transition-all flex items-center gap-1 cursor-pointer">
                <i class="fa-solid fa-user-minus"></i> Remove
              </button>
            </div>
          ` : relation === 'request_sent' ? `
            <div class="p-3.5 bg-amber-950/20 border border-amber-400/50 rounded-xl flex items-center justify-between gap-3">
              <div class="flex items-center gap-2">
                <span class="px-2.5 py-1 rounded-md text-xs font-bold bg-amber-950/60 border border-amber-400/60 text-amber-300 shadow-[0_0_8px_rgba(255,183,3,0.2)] flex items-center gap-1.5">
                  <i class="fa-solid fa-clock-rotate-left text-amber-400"></i> Crew Request Sent
                </span>
                <span class="text-[11px] text-slate-400">Waiting for ${displayName} to accept...</span>
              </div>
              <button id="profile-cancel-crew-btn" class="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-400 border border-white/10 hover:text-white hover:border-white/30 transition-colors cursor-pointer">
                Cancel
              </button>
            </div>
          ` : relation === 'request_received' ? `
            <div class="p-3.5 bg-emerald-950/30 border border-emerald-400/50 rounded-xl space-y-2 shadow-[0_0_12px_rgba(0,255,136,0.15)]">
              <div class="text-xs text-emerald-300 font-bold flex items-center gap-2">
                <i class="fa-solid fa-envelope-open-text text-emerald-400 text-sm"></i>
                <span>${displayName} sent you a link to join each other's Crew!</span>
              </div>
              <div class="flex items-center gap-2.5 pt-1">
                <button id="profile-accept-crew-btn" class="btn-primary px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 cursor-pointer">
                  <i class="fa-solid fa-check"></i> Accept into Crew
                </button>
                <button id="profile-decline-crew-btn" class="px-3.5 py-2 rounded-xl text-xs font-bold text-slate-400 border border-white/10 hover:text-white hover:border-white/30 transition-colors cursor-pointer">
                  Decline
                </button>
              </div>
            </div>
          ` : `
            <div class="p-3.5 bg-[#090e18] border border-cyan-400/40 rounded-xl flex items-center justify-between gap-3 shadow-[0_0_12px_rgba(0,240,255,0.15)]">
              <div>
                <div class="text-xs font-bold text-slate-100 flex items-center gap-1.5">
                  <i class="fa-solid fa-user-plus text-cyan-400"></i> Connect with ${displayName}
                </div>
                <p class="text-[11px] text-slate-400 mt-0.5">Send a link to add each other to your Crew list.</p>
              </div>
              <button id="profile-add-crew-btn" class="btn-primary px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 shadow-[0_0_12px_rgba(0,240,255,0.3)] flex-shrink-0 cursor-pointer">
                <i class="fa-solid fa-user-plus"></i> Add to Crew
              </button>
            </div>
          `}
        </div>

        ${canViewFullDetails ? `
          <!-- Bio & Vibe (Viewable only by self or admins) -->
          <div class="p-4 rounded-xl bg-transparent border border-white/[0.08] text-xs text-slate-300 leading-relaxed space-y-1">
            <strong class="font-bold block uppercase tracking-wider text-[10px] text-cyan-400">Bio & Neighborly Vibe:</strong>
            <p>${targetUser.bio || 'Ready to pitch in and collaborate on physical projects.'}</p>
          </div>

          <!-- The Family Squad (Viewable only by self or admins) -->
          <div class="space-y-2">
            <h4 class="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
              <i class="fa-solid fa-children text-pink-400"></i> The Family Squad (${targetUser.kids?.length || 0})
            </h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              ${(!targetUser.kids || targetUser.kids.length === 0) ? `
                <div class="col-span-full p-3 rounded-lg border border-white/[0.06] text-slate-500 italic text-center text-[11px]">
                  No kids/squad members listed yet.
                </div>
              ` : targetUser.kids.map(k => `
                <div class="p-2.5 rounded-lg border border-white/[0.08] space-y-0.5">
                  <div class="flex items-center justify-between font-bold text-slate-200 text-xs">
                    <span>${k.gender}</span>
                    <span class="text-[10px] px-2 py-0.2 rounded-full border border-pink-500/50 text-pink-400">${k.age}</span>
                  </div>
                  <div class="text-[10px] text-slate-400 truncate">
                    <span class="text-slate-500">Interests:</span> ${k.interest}
                  </div>
                </div>
              `).join('')}
            </div>
          </div>

          <!-- Self-Assessed Skills (Viewable only by self or admins) -->
          <div class="space-y-2">
            <h4 class="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
              <i class="fa-solid fa-screwdriver-wrench text-cyan-400"></i> Peer Skill Interests
            </h4>
            <div class="flex flex-wrap gap-1.5">
              ${(!targetUser.skills || targetUser.skills.length === 0) ? `
                <span class="text-xs text-slate-500 italic">No skills listed yet.</span>
              ` : targetUser.skills.map(s => `
                <span class="px-2.5 py-1 rounded-md text-xs bg-transparent text-cyan-300 border border-cyan-400/30">
                  ${s}
                </span>
              `).join('')}
            </div>
          </div>
        ` : `
          <!-- Privacy Guard Card for Other Members -->
          <div class="p-4 rounded-xl bg-transparent border border-white/[0.08] text-xs text-slate-400 space-y-1.5 text-center">
            <div class="text-cyan-400 font-bold text-xs flex items-center justify-center gap-1.5">
              <i class="fa-solid fa-user-shield"></i> Member Privacy Safeguard
            </div>
            <p class="text-[11px] text-slate-400 leading-relaxed max-w-sm mx-auto">
              Personal profile details (neighborhood, bio, family squad, and skills) are private to this member. Add them to your Crew to link up shoulder-to-shoulder on local builds and events!
            </p>
          </div>
        `}

        <!-- Earned Badges -->
        ${earnedBadges.length > 0 ? `
          <div class="space-y-2 pt-2 border-t border-white/[0.08]">
            <h4 class="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
              <i class="fa-solid fa-medal text-amber-400"></i> Earned Badges (${earnedBadges.length})
            </h4>
            <div class="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
              ${earnedBadges.map(b => `
                <div class="p-2.5 rounded-lg bg-transparent border border-white/[0.08] flex items-center gap-2">
                  <div class="w-8 h-8 rounded-lg bg-transparent border border-cyan-400/40 text-cyan-400 flex items-center justify-center text-sm flex-shrink-0">
                    <i class="fa-solid ${b.icon}"></i>
                  </div>
                  <div class="min-w-0">
                    <div class="font-bold text-slate-200 text-xs truncate">${b.name}</div>
                    <div class="text-[9px] text-slate-500 uppercase">${b.category}</div>
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
        ` : ''}

        <!-- Footer -->
        <div class="pt-3 border-t border-white/[0.08] flex items-center justify-between">
          <div>
            ${relation !== 'self' ? `
              <button id="report-user-btn" class="text-xs text-slate-500 hover:text-amber-400 flex items-center gap-1.5 transition-colors cursor-pointer px-2 py-1 rounded" title="Report a concern regarding this member">
                <i class="fa-solid fa-flag text-[10px]"></i>
                <span>Report Concern</span>
              </button>
            ` : '<div></div>'}
          </div>
          <button id="modal-close-bottom-btn" class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-400 hover:text-white border border-transparent hover:border-white/10 transition-colors cursor-pointer">
            Close
          </button>
        </div>

      </div>
    </div>
  `;
}

export function bindDadProfileModalEvents(
  targetUser: DadProfile,
  onClose: () => void,
  onRefresh: () => void,
  onReportConcern?: (targetUser: DadProfile) => void
): void {
  const displayName = getDadDisplayName(targetUser);

  // Report User
  document.getElementById('report-user-btn')?.addEventListener('click', () => {
    if (onReportConcern) onReportConcern(targetUser);
  });

  // Close triggers
  document.getElementById('close-dad-profile-modal-btn')?.addEventListener('click', onClose);
  document.getElementById('modal-close-bottom-btn')?.addEventListener('click', onClose);

  // Add to Crew
  document.getElementById('profile-add-crew-btn')?.addEventListener('click', async () => {
    try {
      await sendCrewRequest(targetUser.uid);
      showToast(`Crew friend request sent to ${displayName}!`, 'success');
      onRefresh();
    } catch (err: any) {
      showToast(err.message || 'Failed to send request', 'error');
    }
  });

  // Accept into Crew
  document.getElementById('profile-accept-crew-btn')?.addEventListener('click', async () => {
    try {
      await acceptCrewRequest(targetUser.uid);
      showToast(`You and ${displayName} are now in each other's Crew!`, 'success');
      onRefresh();
    } catch (err: any) {
      showToast(err.message || 'Failed to accept request', 'error');
    }
  });

  // Decline request
  document.getElementById('profile-decline-crew-btn')?.addEventListener('click', async () => {
    try {
      await declineCrewRequest(targetUser.uid);
      showToast(`Crew request declined`, 'info');
      onRefresh();
    } catch (err: any) {
      showToast(err.message || 'Failed to decline request', 'error');
    }
  });

  // Cancel sent request
  document.getElementById('profile-cancel-crew-btn')?.addEventListener('click', async () => {
    try {
      await cancelCrewRequest(targetUser.uid);
      showToast(`Crew request canceled`, 'info');
      onRefresh();
    } catch (err: any) {
      showToast(err.message || 'Failed to cancel request', 'error');
    }
  });

  // Remove from Crew
  document.getElementById('profile-remove-crew-btn')?.addEventListener('click', async () => {
    if (confirm(`Remove ${displayName} from your Crew?`)) {
      try {
        await removeFromCrew(targetUser.uid);
        showToast(`${displayName} removed from your Crew`, 'info');
        onRefresh();
      } catch (err: any) {
        showToast(err.message || 'Failed to remove from crew', 'error');
      }
    }
  });
}
